import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { DollarSign, CalendarCheck, Reply, TrendingUp, Trophy, Send, ArrowRight, Percent, Wallet, Mail, Linkedin, AlertTriangle, Clock, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { SetupStrip } from "@/components/SetupStrip";
import { BriefDialog } from "@/components/BriefDialog";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className={`metric text-3xl mt-3 ${accent}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const loadToday = () => api.get("/today").then((r) => setToday(r.data)).catch(() => {});
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); loadToday(); }, []);

  const sendDue = async () => {
    setSending(true);
    try { const { data: r } = await api.post("/today/send-due"); toast.success(`Sent ${r.sent} · ${r.blocked} blocked · ${r.failed} failed`); await loadToday(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not send due emails"); } finally { setSending(false); }
  };

  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const funnelEmpty = funnel.every((f) => !f.value);
  const fmt = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`);

  return (
    <PageBody>
      <PageHeader eyebrow="Command Center" title="Revenue telemetry"
        subtitle="Your funnel, quota attainment, and cost per meeting across this workspace."
        actions={<div className="flex items-center gap-2"><Button variant="outline" onClick={() => window.dispatchEvent(new CustomEvent("dealsops:open-operator"))} className="rounded-md" data-testid="dash-operator-btn"><Bot className="mr-2 h-4 w-4 text-amber-500" /> Operator</Button><BriefDialog variant="outline" /><Button onClick={() => navigate("/app/campaigns")} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="dash-new-campaign">New campaign <ArrowRight className="ml-2 h-4 w-4" /></Button></div>}
      />

      <SetupStrip />

      {today && (today.counts.emails > 0 || today.counts.linkedin > 0 || today.counts.mailboxes_error > 0) && (
        <div className="rounded-xl border border-border/70 bg-card/50 p-5 mb-6" data-testid="today-list">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Today</h3>
              <p className="text-xs text-muted-foreground mt-0.5">What needs you in the next 24 hours across this workspace.</p>
            </div>
            {today.counts.emails > 0 && (
              <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={sendDue} disabled={sending} data-testid="send-due-btn">
                {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Send due emails now
              </Button>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-border/60 p-4" data-testid="today-emails">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><Mail className="h-3.5 w-3.5 text-sky-500" /> Emails due <span className="metric text-sky-500">{today.counts.emails}</span></div>
              {today.emails_due.slice(0, 5).map((e) => (
                <div key={e.id} className="text-xs text-muted-foreground py-1 border-b border-border/40 last:border-0 flex justify-between gap-2">
                  <span className="truncate">{e.contact_name || e.contact_email}</span>
                  <span className={`shrink-0 ${e.overdue ? "text-amber-500" : ""}`}>{e.overdue ? "overdue" : e.send_at_display}</span>
                </div>
              ))}
              {today.counts.emails === 0 && <p className="text-xs text-muted-foreground/70">Nothing due.</p>}
            </div>
            <div className="rounded-lg border border-border/60 p-4" data-testid="today-linkedin">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><Linkedin className="h-3.5 w-3.5 text-sky-500" /> LinkedIn tasks <span className="metric text-sky-500">{today.counts.linkedin}</span></div>
              {today.linkedin_due.slice(0, 5).map((t) => (
                <div key={t.id} className="text-xs text-muted-foreground py-1 border-b border-border/40 last:border-0 flex justify-between gap-2">
                  <span className="truncate">{t.contact_name}</span><span className="shrink-0">{t.due_at_display}</span>
                </div>
              ))}
              {today.counts.linkedin === 0 && <p className="text-xs text-muted-foreground/70">Nothing due.</p>}
              {today.counts.linkedin > 0 && <button onClick={() => navigate("/app/linkedin")} className="text-[11px] text-amber-500 mt-2">Open LinkedIn queue →</button>}
            </div>
            <div className={`rounded-lg border p-4 ${today.counts.mailboxes_error > 0 ? "border-rose-500/40 bg-rose-500/5" : "border-border/60"}`} data-testid="today-mailboxes">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><AlertTriangle className={`h-3.5 w-3.5 ${today.counts.mailboxes_error > 0 ? "text-rose-500" : "text-muted-foreground"}`} /> Mailboxes in Error <span className={`metric ${today.counts.mailboxes_error > 0 ? "text-rose-500" : "text-muted-foreground"}`}>{today.counts.mailboxes_error}</span></div>
              {today.mailboxes_error.map((m) => (
                <div key={m.provider} className="text-xs text-rose-500 py-1 flex justify-between gap-2"><span className="capitalize">{m.provider === "gmail" ? "Google" : "Outlook"}</span><span className="truncate">{m.email}</span></div>
              ))}
              {today.counts.mailboxes_error === 0 ? <p className="text-xs text-muted-foreground/70">All mailboxes healthy.</p>
                : <button onClick={() => navigate("/app/integrations")} className="text-[11px] text-rose-500 mt-2">Reconnect in Integrations →</button>}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="command-center-funnel-metric">
        <Stat icon={DollarSign} label="Cost per meeting" value={`$${k.cost_per_meeting ?? 0}`} accent="text-amber-500" testid="kpi-cpm" />
        <Stat icon={Percent} label="Win rate" value={`${k.win_rate ?? 0}%`} accent="text-emerald-500" testid="kpi-win-rate" />
        <Stat icon={Wallet} label="Avg deal size" value={fmt(k.avg_deal_size ?? 0)} accent="text-amber-500" testid="kpi-avg-deal" />
        <Stat icon={CalendarCheck} label="Meetings booked" value={k.meetings_booked ?? 0} accent="text-emerald-500" testid="kpi-meetings" />
        <Stat icon={Reply} label="Reply rate" value={`${k.reply_rate ?? 0}%`} accent="text-sky-500" testid="kpi-reply" />
        <Stat icon={TrendingUp} label="Open pipeline" value={fmt(k.pipeline_value ?? 0)} accent="text-foreground" testid="kpi-pipeline" />
        <Stat icon={Trophy} label="Closed won" value={fmt(k.won_value ?? 0)} accent="text-emerald-500" testid="kpi-won" />
        <Stat icon={Send} label="Sends this month" value={k.sends ?? 0} accent="text-foreground" testid="kpi-sends" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border/70 bg-card/50 p-6">
          <h3 className="font-display text-lg font-semibold tracking-tight mb-1">Conversion funnel</h3>
          <p className="text-xs text-muted-foreground mb-6">Lead → Won across the active workspace.</p>
          <div className="h-72">
            {funnelEmpty ? (
              <div className="h-full flex flex-col items-center justify-center text-center border border-dashed border-border/60 rounded-lg">
                <p className="text-sm text-muted-foreground">No activity in this workspace yet.</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Build an offer and launch a campaign to populate the funnel.</p>
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "hsl(var(--accent))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {funnel.map((f, i) => <Cell key={f.stage || i} fill={i === funnel.length - 1 ? "hsl(160 84% 39%)" : "hsl(38 92% 50%)"} fillOpacity={0.85 - i * 0.06} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <h3 className="font-display text-lg font-semibold tracking-tight mb-4">Get to first meeting</h3>
          <ol className="space-y-4">
            {[
              ["Describe your offer", "/app/offer", "Generate an ICP brief & angles"],
              ["Build a compliant list", "/app/leads", "Import or source verified contacts"],
              ["Approve first-touch", "/app/approval", "Gate every send before it ships"],
              ["Classify replies & book", "/app/inbox", "Turn interest into meetings"],
            ].map(([t, to, d], i) => (
              <li key={t}>
                <button onClick={() => navigate(to)} className="w-full text-left group flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-amber-500/15 text-amber-500 text-xs font-mono flex items-center justify-center">{i + 1}</span>
                  <span>
                    <span className="text-sm font-medium group-hover:text-amber-500 transition-colors">{t}</span>
                    <span className="block text-xs text-muted-foreground">{d}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </PageBody>
  );
}
